import React, { useState, useEffect } from "react";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { Wallet, Landmark, ArrowDownToLine, Clock, CheckCircle2, X, Plus, Banknote } from "lucide-react";

export function PayoutsPanel() {
  const [data, setData] = useState(null);
  const [showBank, setShowBank] = useState(false);
  const [amount, setAmount] = useState("");
  const [cashing, setCashing] = useState(false);
  const [connect, setConnect] = useState(null);
  const [onboarding, setOnboarding] = useState(false);

  const load = async () => {
    try { const res = await api.get("/host/payouts"); setData(res.data); } catch { /* noop */ }
    try { const s = await api.get("/host/connect/status"); setConnect(s.data); } catch { /* noop */ }
  };
  useEffect(() => { load(); }, []);

  const setupDeposit = async () => {
    setOnboarding(true);
    try {
      const { data: d } = await api.post("/host/connect/onboard", { origin_url: window.location.origin });
      window.location.href = d.url;
    } catch (err) {
      if (err.response?.status === 503) toast.info(formatApiErrorDetail(err.response?.data?.detail));
      else toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setOnboarding(false);
    }
  };

  const cashOut = async () => {
    const amt = Number(amount);
    if (!amt || amt <= 0) { toast.error("Enter a valid amount"); return; }
    setCashing(true);
    try {
      await api.post("/host/payouts", { amount: amt });
      toast.success("Payout requested and processing.");
      setAmount(""); load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setCashing(false); }
  };

  const balance = data?.available_balance ?? 0;
  const bank = data?.bank_account;

  return (
    <div className="rounded-2xl border border-border bg-card p-6" data-testid="payouts-panel">
      <div className="flex items-center gap-2 mb-5"><Wallet size={18} /><h2 className="font-display text-xl font-medium">Earnings & Payouts</h2></div>

      <div className="rounded-xl bg-background border border-border p-5 mb-4">
        <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider">Available balance</p>
        <p className="font-display text-3xl font-bold mt-1" data-testid="available-balance">${balance.toFixed(2)}</p>
      </div>

      <div className="flex items-center justify-between rounded-xl border border-border p-4 mb-4" data-testid="direct-deposit-row">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-accent grid place-items-center"><Banknote size={16} /></div>
          <div>
            <p className="text-sm font-medium">Direct bank deposit</p>
            <p className="text-xs text-muted-foreground">{connect?.payouts_enabled ? "Active — payouts go to your bank" : connect?.connected ? "Finish Stripe setup to activate" : "Powered by Stripe"}</p>
          </div>
        </div>
        {connect?.payouts_enabled ? (
          <span className="text-xs text-emerald-500 flex items-center gap-1"><CheckCircle2 size={14} /> Ready</span>
        ) : (
          <button onClick={setupDeposit} disabled={onboarding} data-testid="setup-deposit-btn" className="text-xs px-4 py-2 rounded-full bg-foreground text-background font-medium hover:opacity-90 transition-opacity disabled:opacity-50">{onboarding ? "…" : connect?.connected ? "Finish setup" : "Set up"}</button>
        )}
      </div>

      {bank ? (
        <div className="flex items-center justify-between rounded-xl border border-border p-4 mb-4">
          <div className="flex items-center gap-3">
            <div className="h-9 w-9 rounded-lg bg-accent grid place-items-center"><Landmark size={16} /></div>
            <div><p className="text-sm font-medium">{bank.bank_name}</p><p className="text-xs text-muted-foreground font-mono">{bank.account_last4} · {bank.account_holder}</p></div>
          </div>
          <button onClick={() => setShowBank(true)} className="text-xs text-muted-foreground hover:text-foreground" data-testid="edit-bank-btn">Edit</button>
        </div>
      ) : (
        <button onClick={() => setShowBank(true)} data-testid="connect-bank-btn"
          className="w-full flex items-center justify-center gap-2 rounded-xl border border-dashed border-border p-4 mb-4 text-sm hover:bg-accent transition-colors">
          <Plus size={16} /> Connect a bank account
        </button>
      )}

      <div className="flex gap-2">
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount ($)" data-testid="payout-amount"
          className="flex-1 h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring" />
        <button onClick={cashOut} disabled={cashing || !bank} data-testid="cash-out-btn"
          className="h-11 px-5 rounded-xl bg-foreground text-background text-sm font-medium flex items-center gap-2 hover:opacity-90 transition-opacity disabled:opacity-40">
          <ArrowDownToLine size={15} /> {cashing ? "…" : "Cash out"}
        </button>
      </div>

      {data?.payouts?.length > 0 && (
        <div className="mt-5 space-y-2" data-testid="payout-history">
          <p className="text-xs text-muted-foreground font-mono uppercase tracking-wider mb-2">History</p>
          {data.payouts.map((p) => (
            <div key={p.payout_id} className="flex items-center justify-between text-sm py-2 border-b border-border last:border-0">
              <span className="flex items-center gap-2 text-muted-foreground">
                {p.status === "paid" ? <CheckCircle2 size={14} className="text-emerald-500" /> : <Clock size={14} className="text-amber-500" />}
                <span>
                  <span className="capitalize text-foreground">{p.status}</span> · {p.destination}
                  {p.estimated_arrival && <span className="block text-xs text-muted-foreground">{p.status === "paid" ? "Deposited" : "Arrives"} ~{new Date(p.estimated_arrival).toLocaleDateString()}</span>}
                </span>
              </span>
              <span className="font-mono font-medium">${p.amount.toFixed(2)}</span>
            </div>
          ))}
        </div>
      )}

      {showBank && <BankForm existing={bank} onClose={() => setShowBank(false)} onSaved={() => { setShowBank(false); load(); }} />}
    </div>
  );
}

function BankForm({ existing, onClose, onSaved }) {
  const [f, setF] = useState({ account_holder: existing?.account_holder || "", bank_name: existing?.bank_name || "", routing_number: "", account_number: "" });
  const [saving, setSaving] = useState(false);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post("/host/bank-account", f); toast.success("Bank account connected"); onSaved(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm">
      <form onSubmit={submit} className="relative w-full max-w-md rounded-2xl border border-border bg-card p-8 rise" data-testid="bank-form">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-muted-foreground hover:text-foreground"><X size={20} /></button>
        <h2 className="font-display text-xl font-bold mb-1 flex items-center gap-2"><Landmark size={18} /> Connect bank account</h2>
        <p className="text-xs text-muted-foreground mb-5">Your details are encrypted and stored securely for payouts.</p>
        <div className="space-y-3">
          <input required value={f.account_holder} onChange={upd("account_holder")} placeholder="Account holder name" data-testid="bank-holder" className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring" />
          <input required value={f.bank_name} onChange={upd("bank_name")} placeholder="Bank name" data-testid="bank-name" className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring" />
          <input required value={f.routing_number} onChange={upd("routing_number")} placeholder="Routing number" data-testid="bank-routing" className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring" />
          <input required value={f.account_number} onChange={upd("account_number")} placeholder="Account number" data-testid="bank-account-number" className="w-full h-11 px-4 rounded-xl bg-background border border-input text-sm outline-none focus:border-ring" />
        </div>
        <button type="submit" disabled={saving} data-testid="bank-save" className="mt-5 w-full h-11 rounded-xl bg-foreground text-background font-medium text-sm hover:opacity-90 transition-opacity disabled:opacity-50">{saving ? "Saving…" : "Save bank account"}</button>
      </form>
    </div>
  );
}
